// AvmContext.tsx
import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { getAvmTopSix, getAvmCompsForMap } from '../../utility/utility_functions';
import useGetMostRecentAvmResults from '../../hooks/api/AvmResults/useGetMostRecentAvmResults';
import { usePropertyContext } from './PropertyContext';
import { AvmContextType } from './DMContextInterfaces';
import { useDecisionFormContext } from './DecisionFormContext';
import { ValuationsType } from '../../utility/types';
import { useGetValuationSettings } from '../../hooks/api/ValuationSettings/useGetValuationSettings';
import { DEFAULT_MARKET_VALUE_RATE, applyMarketValueRateToAvmResult } from '../../utility/valuationSettings';

const AvmContext = createContext<AvmContextType | undefined>(undefined);

export function AvmProvider({ children }: { children: ReactNode }) {
  const { propertyId: activePropertyId } = usePropertyContext();
  const {
    state: {
      valuationType,
      propertyId
    },
    dispatch,
  } = useDecisionFormContext();

  const {
    mostRecentAvmResults,
    isLoadingMostRecentAvmResults,
    errorMostRecentAvmResults,
  } = useGetMostRecentAvmResults(activePropertyId);

  // Wait for the rate so the page never flashes an unadjusted value; fall back to 0% if it can't load.
  const { data: valuationSettings, isLoading: isLoadingValuationSettings } = useGetValuationSettings();
  const marketValueRate = valuationSettings?.market_value_rate ?? DEFAULT_MARKET_VALUE_RATE;

  const [ rawSoldAvmResult, setSoldAvmResult ] = useState(null);
  const [ rawActiveAvmResult, setActiveAvmResult ] = useState(null);
  const [ soldCompsSix, setSoldCompsSix ] = useState([]);
  const [ activeCompsSix, setActiveCompsSix ] = useState([]);
  const [ avmCompsForMap, setAvmCompsForMap ] = useState([]);

  useEffect(() => {
    if (mostRecentAvmResults && propertyId === activePropertyId && !isLoadingValuationSettings) {
      const sold = mostRecentAvmResults.sold_avm_result;
      const active = mostRecentAvmResults.active_avm_result;
      const soldComps = getAvmTopSix(sold);
      const activeComps = getAvmTopSix(active);
      const mapComps = getAvmCompsForMap(activeComps, soldComps);

      setSoldAvmResult(sold);
      setActiveAvmResult(active);
      setSoldCompsSix(soldComps);
      setActiveCompsSix(activeComps);
      setAvmCompsForMap(mapComps);
    }
  }, [ mostRecentAvmResults, propertyId, isLoadingValuationSettings ]);

  // The Sold and Active ARV (value and $/sqft) include the user's Market Value Rate; comps stay as calculated.
  const soldAvmResult = useMemo(
    () => applyMarketValueRateToAvmResult(rawSoldAvmResult, marketValueRate),
    [ rawSoldAvmResult, marketValueRate ]
  );
  const activeAvmResult = useMemo(
    () => applyMarketValueRateToAvmResult(rawActiveAvmResult, marketValueRate),
    [ rawActiveAvmResult, marketValueRate ]
  );

  const soldAvmEstimatedValue = useMemo(() => soldAvmResult?.result?.estimated_value ?? null, [ soldAvmResult ]);
  const soldAvmEstimatedValuePerSqft = useMemo(() => soldAvmResult?.result?.estimated_dollar_per_sqft ?? null, [ soldAvmResult ]);
  const activeAvmEstimatedValue = useMemo(() => activeAvmResult?.result?.estimated_value ?? null, [ activeAvmResult ]);
  const activeAvmEstimatedValuePerSqft = useMemo(() => activeAvmResult?.result?.estimated_dollar_per_sqft ?? null, [ activeAvmResult ]);

  useEffect(() => {
    // If we are using the inactive avm, we need to set the estimated value and estimated value per sqft
    if (valuationType === ValuationsType.InactiveAvm && soldAvmEstimatedValue && soldAvmEstimatedValuePerSqft) {
      dispatch({ type: 'INITIALIZE_FORM_KEY', payload: [
        { field: 'estimatedValue', value: soldAvmEstimatedValue },
        { field: 'estimatedValuePerSqft', value: soldAvmEstimatedValuePerSqft },
      ] });
    }
    // eslint-disable-next-line
  }, [ soldAvmEstimatedValue, soldAvmEstimatedValuePerSqft, valuationType ]);
  return (
    <AvmContext.Provider
      value={{
        soldAvmResult,
        activeAvmResult,
        soldCompsSix,
        activeCompsSix,
        avmCompsForMap,
        soldAvmEstimatedValue,
        soldAvmEstimatedValuePerSqft,
        activeAvmEstimatedValue,
        activeAvmEstimatedValuePerSqft,
        marketValueRate,
        isLoadingMostRecentAvmResults,
        errorMostRecentAvmResults,
      }}>
      {children}
    </AvmContext.Provider>
  );
}

export function useAvmContext() {
  const ctx = useContext(AvmContext);
  if (!ctx) throw new Error('useAvmContext must be used within AvmProvider');
  return ctx;
}
